import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash, Plus, Minus, MagnifyingGlass } from "@phosphor-icons/react";

export default function POS() {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]); // {item, qty, notes}
  const [orderType, setOrderType] = useState("dine-in");
  const [tableId, setTableId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0.05);
  const [serviceRate, setServiceRate] = useState(0);
  const [payOpen, setPayOpen] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/menu", { params: { available_only: true } }).then((r) => setItems(r.data));
    api.get("/tables").then((r) => {
      setTables(r.data);
      const pre = searchParams.get("table");
      if (pre && r.data.some((t) => t.id === pre)) {
        setTableId(pre);
        setOrderType("dine-in");
      }
    });
  }, [searchParams]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (activeCat !== "all" && it.category_id !== activeCat) return false;
      if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, activeCat, q]);

  const addToCart = (item) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.item.id === item.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + 1 };
        return copy;
      }
      return [...prev, { item, qty: 1, notes: "" }];
    });
  };
  const inc = (id) => setCart((c) => c.map((x) => x.item.id === id ? { ...x, qty: x.qty + 1 } : x));
  const dec = (id) => setCart((c) => c.map((x) => x.item.id === id ? { ...x, qty: Math.max(1, x.qty - 1) } : x));
  const rm = (id) => setCart((c) => c.filter((x) => x.item.id !== id));

  const subtotal = cart.reduce((s, c) => s + c.item.price * c.qty, 0);
  const afterDiscount = Math.max(0, subtotal - discount);
  const tax = afterDiscount * taxRate;
  const service = afterDiscount * serviceRate;
  const total = afterDiscount + tax + service;

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.get(`/coupons/validate/${coupon}`);
      if (subtotal < (data.min_order || 0)) {
        toast.error(`Minimum order ${fmtMoney(data.min_order)} required`);
        return;
      }
      const d = data.discount_type === "percent" ? subtotal * data.discount_value / 100 : data.discount_value;
      setDiscount(d);
      toast.success(`Coupon ${data.code} applied: -${fmtMoney(d)}`);
    } catch {
      toast.error("Invalid or inactive coupon");
    }
  };

  const placeOrder = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (orderType === "dine-in" && !tableId) return toast.error("Select a table");
    setBusy(true);
    try {
      const payload = {
        type: orderType,
        table_id: orderType === "dine-in" ? tableId : null,
        customer_name: customerName || null,
        customer_phone: customerPhone || null,
        items: cart.map((c) => ({ item_id: c.item.id, quantity: c.qty, addons: [], notes: c.notes })),
        discount,
        coupon_code: coupon || null,
        tax_rate: taxRate,
        service_charge_rate: serviceRate,
      };
      const { data } = await api.post("/orders", payload);
      setLastOrder(data);
      setPayOpen(true);
      toast.success(`Order ${data.order_number} placed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to place order");
    } finally {
      setBusy(false);
    }
  };

  const completePayment = async (method) => {
    if (!lastOrder) return;
    try {
      await api.post(`/orders/${lastOrder.id}/pay`, { method });
      toast.success("Payment recorded");
      setPayOpen(false);
      setLastOrder(null);
      // reset
      setCart([]); setCoupon(""); setDiscount(0); setCustomerName(""); setCustomerPhone(""); setTableId("");
      // refresh tables
      api.get("/tables").then((r) => setTables(r.data));
    } catch (e) {
      toast.error("Payment failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="POS · Billing"
        title="Take an order"
        subtitle="Tap items to build the bill. Switch order type, attach a table, apply coupons, and collect payment in seconds."
      />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Menu side */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search + categories */}
          <div className="bento p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search menu…"
                className="pl-9"
                data-testid="pos-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setActiveCat("all")}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${activeCat === "all" ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)]"}`}
                data-testid="pos-cat-all"
              >All</button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold ${activeCat === c.id ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)]"}`}
                  data-testid={`pos-cat-${c.name.toLowerCase()}`}
                >{c.name}</button>
              ))}
            </div>
          </div>

          {/* Item grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 stagger" data-testid="pos-menu-grid">
            {filtered.map((it) => (
              <button
                key={it.id}
                onClick={() => addToCart(it)}
                className="tile text-left overflow-hidden flex flex-col"
                data-testid={`pos-item-${it.id}`}
              >
                <div className="h-28 bg-[var(--bg-secondary)] overflow-hidden">
                  {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />}
                </div>
                <div className="p-3">
                  <div className="text-sm font-semibold leading-tight">{it.name}</div>
                  <div className="flex justify-between items-end mt-2">
                    <div className="font-display font-extrabold text-[var(--brand)]">{fmtMoney(it.price)}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--text-secondary)]">{it.is_veg ? "Veg" : "Non-veg"}</div>
                  </div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-sm text-[var(--text-secondary)] py-8 text-center">No menu items match.</div>
            )}
          </div>
        </div>

        {/* Cart side */}
        <div className="bento p-5 sticky top-4 self-start" data-testid="pos-cart">
          <div className="label-eyebrow mb-2">Current order</div>
          <h3 className="font-display font-bold text-xl mb-4">Cart ({cart.length})</h3>

          {/* Order type */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {["dine-in", "takeaway", "delivery"].map((t) => (
              <button
                key={t}
                onClick={() => setOrderType(t)}
                className={`py-2 rounded-md text-xs font-bold capitalize ${orderType === t ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)]"}`}
                data-testid={`pos-type-${t}`}
              >{t}</button>
            ))}
          </div>

          {/* Table picker (dine-in) */}
          {orderType === "dine-in" && (
            <div className="mb-3">
              <Label className="label-eyebrow">Table</Label>
              <Select value={tableId} onValueChange={setTableId}>
                <SelectTrigger className="mt-1" data-testid="pos-table-select"><SelectValue placeholder="Select table" /></SelectTrigger>
                <SelectContent>
                  {tables.filter((t) => t.status !== "occupied" || t.id === tableId).map((t) => (
                    <SelectItem key={t.id} value={t.id}>Table {t.number} · {t.section} · {t.capacity} seats</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Customer */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Input placeholder="Customer name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="pos-customer-name" />
            <Input placeholder="Phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} data-testid="pos-customer-phone" />
          </div>

          {/* Items list */}
          <div className="max-h-64 overflow-auto -mx-2 px-2 mb-3 space-y-2" data-testid="pos-cart-items">
            {cart.map((c) => (
              <div key={c.item.id} className="flex items-center gap-2 py-2 border-b border-[#EAE7E0]/60">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.item.name}</div>
                  <div className="text-[11px] text-[var(--text-secondary)]">{fmtMoney(c.item.price)} ea</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => dec(c.item.id)} className="w-6 h-6 rounded bg-[var(--bg-secondary)] grid place-items-center" data-testid={`pos-qty-dec-${c.item.id}`}><Minus size={12} /></button>
                  <div className="w-6 text-center text-sm font-bold">{c.qty}</div>
                  <button onClick={() => inc(c.item.id)} className="w-6 h-6 rounded bg-[var(--bg-secondary)] grid place-items-center" data-testid={`pos-qty-inc-${c.item.id}`}><Plus size={12} /></button>
                </div>
                <div className="w-16 text-right text-sm font-bold">{fmtMoney(c.item.price * c.qty)}</div>
                <button onClick={() => rm(c.item.id)} className="text-[var(--brand)]" data-testid={`pos-rm-${c.item.id}`}><Trash size={14} /></button>
              </div>
            ))}
            {cart.length === 0 && (
              <div className="text-xs text-[var(--text-secondary)] py-8 text-center">Tap items to add</div>
            )}
          </div>

          {/* Coupon */}
          <div className="flex gap-2 mb-3">
            <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="pos-coupon-input" />
            <Button variant="outline" onClick={applyCoupon} data-testid="pos-apply-coupon">Apply</Button>
          </div>

          {/* Totals */}
          <div className="space-y-1 text-sm mb-3" data-testid="pos-totals">
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Subtotal</span><span className="font-semibold">{fmtMoney(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-[var(--brand-2)]"><span>Discount</span><span>-{fmtMoney(discount)}</span></div>}
            <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Tax (5%)</span><span>{fmtMoney(tax)}</span></div>
            {serviceRate > 0 && <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Service</span><span>{fmtMoney(service)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-[#EAE7E0]"><span className="font-display font-bold text-base">Total</span><span className="font-display font-extrabold text-xl text-[var(--brand)]" data-testid="pos-total">{fmtMoney(total)}</span></div>
          </div>

          <Button onClick={placeOrder} disabled={busy || cart.length === 0} className="w-full btn-primary h-11 rounded-md font-semibold" data-testid="pos-place-order-btn">
            {busy ? "Placing…" : "Place order"}
          </Button>
        </div>
      </div>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pos-payment-dialog">
          <DialogHeader>
            <DialogTitle>Collect payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bento p-4">
              <div className="label-eyebrow">Order</div>
              <div className="font-display font-bold text-lg">{lastOrder?.order_number}</div>
              <div className="font-display font-extrabold text-3xl text-[var(--brand)]">{fmtMoney(lastOrder?.total)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["cash", "upi", "card", "wallet"].map((m) => (
                <Button key={m} onClick={() => completePayment(m)} variant="outline" className="h-12 capitalize" data-testid={`pos-pay-${m}`}>
                  {m}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPayOpen(false); setCart([]); setCoupon(""); setDiscount(0); }} data-testid="pos-pay-later">Pay later</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
